import { Node } from 'prosemirror-model'
import { textboxSchema } from './textboxSchema'

// The text box uses its own scoped schema (see textboxSchema), NOT prose-core's. Stored docs may be in
// either shape: new docs are authored with textboxSchema; older designs hold prose-core docs (headings,
// multi-block list items, marks the scoped schema doesn't define). `normalizeForTextboxSchema` coerces
// any incoming JSON into the scoped shape so both parse — headings become paragraphs, a list item's
// blocks merge into its single paragraph, and unknown marks/nodes are dropped/flattened to text.

interface RawNode {
  type?: string
  content?: RawNode[]
  text?: string
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>
  attrs?: Record<string, unknown>
}

const ALLOWED_MARKS = new Set(['strong', 'em', 'underline', 'strike', 'colored', 'font_weight'])

const depthAttrs = (node: RawNode): Record<string, unknown> | undefined =>
  typeof node.attrs?.depth === 'number' ? { depth: node.attrs.depth } : undefined

// Bold is stored as a single font_weight channel (see activeProseView's ProseWeight), so collapse a legacy
// `strong` mark to font_weight:700 on ingestion — dropping it when an explicit font_weight already sets the
// run's weight (that weight wins). Keeps stored + pasted docs on one representation the toolbar can edit.
const normalizeMarks = (
  marks: Array<{ type: string; attrs?: Record<string, unknown> }> | undefined,
): Array<{ type: string; attrs?: Record<string, unknown> }> | undefined => {
  if (!marks) return marks
  const kept = marks.filter((mark) => ALLOWED_MARKS.has(mark.type))
  const hasFontWeight = kept.some((mark) => mark.type === 'font_weight')
  return kept
    .filter((mark) => !(mark.type === 'strong' && hasFontWeight))
    .map((mark) => (mark.type === 'strong' ? { type: 'font_weight', attrs: { weight: 700 } } : mark))
}

// Inline content: keep text nodes (with only the marks the scoped schema defines); drop everything else
// (inline images, …) that the text box doesn't model.
const normalizeInline = (content: RawNode[] | undefined): RawNode[] =>
  (content ?? [])
    .filter((child) => child.type === 'text' && typeof child.text === 'string')
    .map((child) => ({
      type: 'text',
      text: child.text,
      marks: normalizeMarks(child.marks),
    }))

// The scoped schema has no hard_break node, so a soft line break inside a paragraph/heading would vanish
// on flatten. Split on it into one paragraph per line instead, preserving the break as a real block edge.
const splitInlineOnBreaks = (content: RawNode[] | undefined, attrs?: Record<string, unknown>): RawNode[] => {
  const lines: RawNode[][] = [[]]
  for (const child of content ?? []) {
    if (child.type === 'hard_break') lines.push([])
    else if (child.type === 'text' && typeof child.text === 'string')
      lines[lines.length - 1].push({ type: 'text', text: child.text, marks: normalizeMarks(child.marks) })
  }
  return lines.map((inline) => ({ type: 'paragraph', attrs, content: inline }))
}

const BLOCKISH = new Set([
  'paragraph',
  'heading',
  'table',
  'table_row',
  'table_cell',
  'list_item',
  'ordered_list',
  'unordered_list',
  'blockquote',
])

// Salvage text from an unmodelled block, joining child BLOCKS with newlines (so table cells / stacked
// blocks don't run together) but inline runs with nothing (a paragraph's own runs are one line).
const extractText = (node: RawNode): string => {
  if (node.type === 'text') return node.text ?? ''
  if (node.type === 'hard_break') return '\n'
  const children = node.content ?? []
  const sep = children.some((child) => BLOCKISH.has(child.type ?? '')) ? '\n' : ''
  return children.map(extractText).join(sep)
}

// A list item collapses to exactly one paragraph holding all its blocks' inline content, with a space
// between merged blocks so adjacent paragraphs don't run together.
const normalizeListItem = (node: RawNode): RawNode => {
  const inline: RawNode[] = []
  for (const child of node.content ?? []) {
    if (child.type === 'paragraph' || child.type === 'heading') {
      if (inline.length) inline.push({ type: 'text', text: ' ' })
      inline.push(...normalizeInline(child.content))
    }
  }
  return { type: 'list_item', attrs: depthAttrs(node), content: [{ type: 'paragraph', content: inline }] }
}

const normalizeBlock = (node: RawNode): RawNode[] => {
  switch (node.type) {
    case 'paragraph':
    case 'heading':
      return splitInlineOnBreaks(node.content, depthAttrs(node))
    case 'unordered_list':
    case 'ordered_list': {
      const items = (node.content ?? [])
        .filter((child) => child.type === 'list_item' || child.type === 'vote_list_item')
        .map(normalizeListItem)
      return items.length ? [{ type: node.type, attrs: node.attrs, content: items }] : []
    }
    default: {
      // Unknown block (table, toggle, image, …): salvage its text into a paragraph, or drop if empty.
      const text = extractText(node)
      return text ? [{ type: 'paragraph', content: [{ type: 'text', text }] }] : []
    }
  }
}

export const normalizeForTextboxSchema = (json: RawNode): RawNode => {
  // flatMap so a paragraph that split on hard_break contributes multiple blocks. Leave an empty result
  // empty: parseRichText treats a zero-size doc as null so callers fall back to plain text.
  return { type: 'doc', content: (json.content ?? []).flatMap(normalizeBlock) }
}

// Parse a stored doc into the scoped schema, returning null on anything unusable so callers fall back to
// plain text. Input is normalized first, so both scoped and legacy prose-core docs parse.
export const parseRichText = (richText?: unknown): Node | null => {
  if (!richText || typeof richText !== 'object') return null
  try {
    const doc = textboxSchema.nodeFromJSON(normalizeForTextboxSchema(richText as RawNode))
    return doc.content.size === 0 ? null : doc
  } catch {
    return null
  }
}

export const buildRichTextDoc = (richText?: Record<string, unknown> | null, text?: string): Node => {
  const parsed = parseRichText(richText)
  if (parsed) return parsed
  const paragraph = text
    ? textboxSchema.nodes.paragraph.create(null, textboxSchema.text(text))
    : textboxSchema.nodes.paragraph.create(null)
  return textboxSchema.nodes.doc.create(null, paragraph)
}

// Flatten to plain text (the `text` prop, and via it the box height measurement). The scoped schema
// forces no trailing paragraph, so there is nothing to strip — every block is real content.
export const flattenRichTextDoc = (doc: Node): string => doc.textBetween(0, doc.content.size, '\n')

export interface FontCapability {
  weights: number[]
  italicWeights: number[]
}

// Drop per-run weight marks the font can't render — a font_weight (or legacy strong) whose weight the
// family lacks, including a bold-italic combo with no matching italic face. Italic@400 is always available
// (see useAvailableItalic), so dropping an incompatible weight collapses a bad bold-italic to 400-italic
// rather than dropping the em. Only the weight mark is removed; text and other marks (em, colour, …) stay.
// Returns the SAME object when nothing changed, so onBeforeUpdate skips a needless rewrite. Callers should
// skip custom fonts (which render every weight) and unknown fonts (capability unknown — don't destroy).
export const reconcileRichTextForFont = (
  richText: Record<string, unknown>,
  font: FontCapability,
): Record<string, unknown> => {
  const renderable = (weight: number, italic: boolean): boolean =>
    italic ? weight === 400 || font.italicWeights.includes(weight) : font.weights.includes(weight)

  const walk = (node: unknown): unknown => {
    if (!node || typeof node !== 'object') return node
    const n = node as { marks?: Array<{ type: string; attrs?: Record<string, unknown> }>; content?: unknown[] }
    let out = n
    if (Array.isArray(n.marks)) {
      const italic = n.marks.some((mark) => mark.type === 'em')
      const kept = n.marks.filter((mark) => {
        if (mark.type === 'font_weight') return renderable(Number(mark.attrs?.weight ?? 400), italic)
        if (mark.type === 'strong') return renderable(700, italic)
        return true
      })
      if (kept.length !== n.marks.length) out = { ...n, marks: kept }
    }
    if (Array.isArray(out.content)) {
      const content = out.content.map(walk)
      if (content.some((child, i) => child !== (out.content as unknown[])[i])) out = { ...out, content }
    }
    return out
  }

  return walk(richText) as Record<string, unknown>
}
